import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, EMPTY, map, Observable, tap} from 'rxjs';
import {WaiterAuthService} from 'src/app/auth/services/waiter-auth/waiter-auth.service';
import {APP_ROUTE_PATH, ERROR_TYPE} from 'src/app/core/enums';
import {GlobalErrorResponse} from 'src/app/core/interfaces/global-error.model';
import {Logger} from 'src/app/core/interfaces/Logger';

/**
 * Sign Out Waiter Use Case
 * Handles the process of signing out a waiter from the system and redirecting to the waiter page.
 * This use case encapsulates the business logic for waiter sign out operations including:
 * - Calling the waiter auth service to perform sign out
 * - Navigation to the appropriate route after sign out
 * - Error handling for offline scenarios
 */
@Injectable({providedIn: 'root'})
export class SignOutWaiterUseCase {
  private readonly waiterAuthService = inject(WaiterAuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(Logger);

  execute(): Observable<void> {
    return this.waiterAuthService.signOutWaiter().pipe(
      tap(() => {
        this.navigateToWaiterPage();
      }),
      catchError((error: GlobalErrorResponse) => {
        this.handleSignOutError(error);

        return EMPTY;
      }),
      map(() => void 0)
    );
  }

  private navigateToWaiterPage(): void {
    this.router.navigate([`/${APP_ROUTE_PATH.serviceConfiguration}`], {
      replaceUrl: true,
    });
  }

  private handleSignOutError(error: GlobalErrorResponse): void {
    if (error.message.includes(ERROR_TYPE.offline)) {
      this.navigateToWaiterPage();
    } else {
      this.logger.error(
        new Error('[SignOutWaiterUseCase] Error signing out waiter'),
        error
      );
      this.navigateToWaiterPage();
    }
  }
}
